// src/components/ItemsInventory.js
import React, { useEffect, useMemo, useState } from "react";
import "../index.css";
import LoadingSpinner from "./LoadingSpinner";
import { getNameStyleCSS } from "./GameNameStyles";
import { getSkinSrc } from "./SeasonalSkins";
import { motion, AnimatePresence } from "framer-motion";
import CardPicker from "./CardPicker";

const API_BASE = process.env.REACT_APP_API_URL;

function normalizeItemType(type) {
  const s = String(type ?? "").trim().toLowerCase();

    if ([
    "trophyshield",
    "trophy_shield",
    "trophy-shield"
  ].includes(s)) {
    return "trophyShield";
  }

  if (
    [
      "cardpieces",
      "cardpiece",
      "card_pieces",
      "card-pieces",
    ].includes(s)
  ) {
    return "cardPieces";
  }

  if (
    [
      "spin",
      "spins",
      "fortune_spin",
      "fortune_spins",
      "fortune-wheel",
      "fortune_wheel",
    ].includes(s)
  ) {
    return "spins";
  }

  if (
    [
      "seasonalaccessory",
      "seasonal_accessory",
      "seasonal-accessory",
      "skin",
      "skins",
    ].includes(s)
  ) {
    return "seasonal_accessory";
  }

  if (
    [
      "name",
      "cosmetic",
      "name_cosmetic",
    ].includes(s)
  ) {
    return "name";
  }

  if (["rune", "runes"].includes(s)) {
    return "runes";
  }

  if (["spark", "sparks"].includes(s)) {
    return "sparks";
  }

  if (["booster", "boosters"].includes(s)) {
    return "booster";
  }

  if (["magic_potion", "magic-potion", "magicpotion", "potion"].includes(s)) {
  return "magic_potion";
}

  if (["ability", "abilities"].includes(s)) {
    return "ability";
  }

  if (["card", "cards"].includes(s)) {
    return "card";
  }

  if (["tapi"].includes(s)) {
    return "tapi";
  }

  if (["evolve", "evolvement"].includes(s)) {
    return "evolve";
  }

  return s;
}

function normalizeChestKey(key) {
  const s = String(key ?? "").trim().toLowerCase();
  if (s.includes("wood")) return "wood";
  if (s.includes("silver")) return "silver";
  if (s.includes("gold")) return "golden";
  if (s.includes("diamond")) return "diamond";
  return "wood";
}

const CHESTS_META = {
  wood: { label: "Dustbound", image: "shop_chest_5.png" },
  silver: { label: "Cursed Tomb", image: "shop_chest_3.png" },
  golden: { label: "Lunar Horn", image: "shop_chest_1.png" },
  diamond: { label: "Solar Horizon", image: "shop_chest_2.png" },
};

function getChestMetaFromChestType(chestType) {
  return CHESTS_META[normalizeChestKey(chestType)];
}

function chestTypeToDisplay(type) {
  const s = String(type || "").toLowerCase();

  if (s.includes("legendary")) return "Legendary Chest";
  if (s.includes("epic")) return "Epic Chest";
  if (s.includes("rare")) return "Rare Chest";
  if (s.includes("common")) return "Common Chest";
  if (s.includes("abilities")) return "Ability Chest";
  if (s.includes("boosters")) return "Booster Chest";
  if (s.includes("skins")) return "Skin Chest";

  return "Chest";
}

function getItemName(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);

if (type === "chest") {
  // Arena chest
  if (inv?.chest?.id) {
    const chestType =
      inv?.chest?.chestType ??
      item?.chest_type ??
      item?.chestType ??
      "";

    const meta = getChestMetaFromChestType(chestType);

    return meta?.label
      ? `${meta.label} Chest`
      : chestTypeToDisplay(chestType);
  }

  // Admin Item chest
  return (
    item?.display_name ||
    item?.name ||
    "Chest"
  );
}

  if (type === "cardPieces") {
    return (
      item.card_name ||
      item.cardName ||
      inv.card_name ||
      inv.cardName ||
      "Card Pieces"
    );
  }

  if (type === "sparks") {
    return `${Number(item.sparks_amount || inv.amount || 0).toLocaleString()} Sparks`;
  }

  if (type === "runes") {
    return `${Number(item.runes_amount || inv.amount || 0).toLocaleString()} Runes`;
  }

  if (type === "magic_potion") {
  return item.display_name || item.name || "Magic Potion";
}

  if (type === "ability" && item.ability_name) return item.ability_name;
  if (type === "booster" && item.booster_multiplier) {
    return `${item.booster_multiplier}x Booster`;
  }

  return (
    item.display_name ||
    item.name ||
    item.accessory_name ||
    item.ability_name ||
    "Item"
  );
}

function normalizeRarity(rarity) {
  const r = String(rarity || "").trim().toLowerCase();

  if (r === "legendary") return "legendary";
  if (r === "epic") return "epic";
  if (r === "rare") return "rare";
  return "common";
}

function rarityToIcon(rarity) {
  switch (normalizeRarity(rarity)) {
    case "legendary":
      return "/legendary.png";
    case "epic":
      return "/epic.png";
    case "rare":
      return "/rare.png";
    case "common":
    default:
      return "/common.png";
  }
}

function getCardRarityFromInv(inv) {
  const item = inv?.item || {};

  const direct =
    item.card_rarity ??
    item.cardRarity ??
    item.rarity ??
    item.card?.rarity ??
    inv.card_rarity ??
    inv.rarity;

  if (direct) return direct;

  const name = String(item.display_name || item.name || "").toLowerCase();

  if (name.includes("legendary")) return "legendary";
  if (name.includes("epic")) return "epic";
  if (name.includes("rare")) return "rare";
  if (name.includes("common")) return "common";

  return "common";
}

function getItemIconSrc(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);

  switch (type) {
    case "runes":
      return "/runes.png";

    case "sparks":
      return "/sparks.png";

      case "magic_potion":
  return "/magic_potion.png";

    case "booster":
      return "/energy.png";

    case "spins":
      return "/fortuneicon.png";

    case "tapi":
      return "/icon_tapi.png";

    case "evolve":
      return "/evolveicon.png";

    case "seasonal_accessory":
      return "/fragment.png";

    case "shield":
      return "/shield.png";

    case "ability":
      return "/fightlog.png";

    case "gamepass":
      return "/passicon.png";

    case "cardPieces":
      return "/pieces.png";

        case "card":
      return rarityToIcon(getCardRarityFromInv(inv));

    case "bundle": {
      const customImage =
        item?.image_url ||
        item?.imageUrl;

      return customImage || "/pack.png";
    }

    case "name": {
      const nameCategory = String(
        item?.name_category ||
          item?.nameCategory ||
          ""
      ).toLowerCase();

      const cosmeticValue = String(
        item?.name_value ||
          item?.nameValue ||
          item?.image ||
          item?.icon ||
          ""
      ).trim();

      if (cosmeticValue) {
        if (
          cosmeticValue.startsWith("/") ||
          cosmeticValue.startsWith("http://") ||
          cosmeticValue.startsWith("https://")
        ) {
          return cosmeticValue;
        }

        return `/name-cosmetics/${cosmeticValue}`;
      }

      if (
        nameCategory === "banners" ||
        nameCategory === "banner"
      ) {
        return "/banner_icon.png";
      }

      if (
        nameCategory === "emogy" ||
        nameCategory === "emoji"
      ) {
        return "/emogy_icon.png";
      }

      if (
        nameCategory === "styles" ||
        nameCategory === "style"
      ) {
        return "/style_icon.png";
      }

      return "/style_icon.png";
    }

   case "chest": {
  // Admin Item chest
  if (!inv?.chest?.id) {
    const customImage =
      item?.image_url ||
      item?.imageUrl;

    if (customImage) {
      return customImage;
    }

    return "/pack.png";
  }

  // Arena chest
  const chestType =
    inv?.chest?.chestType ??
    item?.chest_type ??
    item?.chestType ??
    "";

  const meta =
    getChestMetaFromChestType(chestType);

  return meta?.image
    ? `/${meta.image}`
    : "/pack.png";
}

    default:
      return "/pack.png";
  }
}

function getItemQuantity(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);

  if (type === "sparks") {
    return Number(item.sparks_amount ?? inv?.amount ?? inv?.quantity ?? 0) || 0;
  }

  if (type === "runes") {
    return Number(item.runes_amount ?? inv?.amount ?? inv?.quantity ?? 0) || 0;
  }

  return (
    Number(
      inv?.quantity ??
        inv?.amount ??
        item?.quantity ??
        item?.amount ??
        item?.pieces_amount ??
        item?.card_pieces_amount ??
        item?.cardPiecesAmount ??
        item?.fragments ??
        0
    ) || 0
  );
}


function isNameStyleItem(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);
  const category = String(
    item?.name_category || item?.nameCategory || ""
  )
    .trim()
    .toLowerCase();

  return (
    type === "name" &&
    (category === "styles" || category === "style")
  );
}

function NameStyleSample({ inv }) {
  const styleValue = String(
    inv?.item?.name_value ||
      inv?.item?.nameValue ||
      ""
  ).trim();

  return (
    <div
      style={{
        width: "100%",
        height: 72,
        marginTop: -12,
        marginBottom: -7,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 5px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: "100%",
          fontSize: 16,
          lineHeight: 1.1,
          fontWeight: 800,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...getNameStyleCSS(styleValue),
        }}
      >
        TapTopia
      </div>
    </div>
  );
}

function getItemIconStyle(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);

  const nameCategory = String(
    item?.name_category || item?.nameCategory || ""
  )
    .trim()
    .toLowerCase();

  if (type === "name") {
    if (nameCategory === "styles" || nameCategory === "style") {
      return {
        width: 72,
        height: 72,
        maxWidth: 72,
        maxHeight: 72,
        marginTop: -12,
        marginBottom: -7,
      };
    }

    if (
      nameCategory === "emogy" ||
      nameCategory === "emoji" ||
      nameCategory === "emojy"
    ) {
      return {
        width: 46,
        height: 46,
        maxWidth: 46,
        maxHeight: 46,
        marginTop: 0,
        marginBottom: 5,
      };
    }

    if (
      nameCategory === "banners" ||
      nameCategory === "banner"
    ) {
      return {
        width: 90,
        height: 90,
        maxWidth: 90,
        maxHeight: 90,
        marginTop: -21,
        marginBottom: -16,
      };
    }
  }

  return {
    width: 48,
    height: 48,
    maxWidth: 48,
    maxHeight: 48,
    marginTop: 0,
    marginBottom: 5,
  };
}

function getItemQuantityLabel(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);
  const qty = getItemQuantity(inv);

  if (type === "sparks") return qty > 0 ? `${qty.toLocaleString()} Sparks` : "";
  if (type === "runes") return qty > 0 ? `${qty.toLocaleString()} Runes` : "";

  if (qty > 1) return `x${qty}`;

  return "";
}

function needsCardSelection(inv) {
  const type = normalizeItemType(inv?.item?.type);

  return (
    type === "ability" ||
    type === "seasonal_accessory" ||
    type === "cardPieces" ||
    type === "evolve"
  );
}

function isChestRow(inv) {
  return normalizeItemType(inv?.item?.type) === "chest";
}

function isArenaChestRow(inv) {
  return (
    isChestRow(inv) &&
    Boolean(inv?.chest?.id)
  );
}

function isItemChestRow(inv) {
  return (
    isChestRow(inv) &&
    !inv?.chest?.id &&
    Boolean(inv?.user_item_id)
  );
}

function buildChestRows(chests) {
  return (Array.isArray(chests) ? chests : []).map((chest) => ({
    user_item_id: `chest:${chest.id}`,
    quantity: 1,
    claimed: false,
    chest,
    item: {
      type: "chest",
      display_name: chestTypeToDisplay(chest.chestType),
      chest_type: chest.chestType,
      unlock_at: chest.unlockAt,
      created_at: chest.createdAt,
    },
  }));
}

function isNameCosmetic(inv) {
  return normalizeItemType(inv?.item?.type) === "name";
}

function getNameCosmeticColumn(inv) {
  const category = String(
    inv?.item?.name_category ||
      inv?.item?.nameCategory ||
      ""
  )
    .trim()
    .toLowerCase();

  if (category === "styles" || category === "style") {
    return "active_name_style";
  }

  if (category === "banners" || category === "banner") {
    return "active_name_banner";
  }

  if (
    category === "emogy" ||
    category === "emoji"
  ) {
    return "active_name_emogy";
  }

  return null;
}

function getNameCosmeticValue(inv) {
  return String(
    inv?.item?.name_value ||
      inv?.item?.nameValue ||
      ""
  ).trim();
}

function isNameCosmeticActive(inv, user) {
  const column = getNameCosmeticColumn(inv);

  if (!column) {
    return false;
  }

  const activeValue = String(user?.[column] || "").trim();
  const itemValue = getNameCosmeticValue(inv);

  return Boolean(
    activeValue &&
      itemValue &&
      activeValue === itemValue
  );
}

function getSeasonalFragmentCount(inv) {
  const item = inv?.item || inv || {};

  return Number(
    item?.accessory_total_items ??
    item?.accessoryTotalItems ??
    item?.total_fragments ??
    item?.totalFragments ??
    inv?.accessory_total_items ??
    inv?.accessoryTotalItems ??
    inv?.total_fragments ??
    inv?.totalFragments ??
    0
  );
}

function getSeasonalItemSeason(inv) {
  const item = inv?.item || inv || {};

  return (
    item?.season_name ??
    item?.seasonName ??
    inv?.season_name ??
    inv?.seasonName ??
    null
  );
}

function SeasonalSkinItemPreview({ inv, user, size = 76 }) {
  const seasonName = getSeasonalItemSeason(inv);

  const rawSide = String(user?.side || "").toLowerCase();
  const side =
    rawSide === "bad" || rawSide === "moon"
      ? "moon"
      : "sun";

  const parts = [
    "body",
    "pants",
    "shirt",
    "boots",
    "neck",
    "head",
    "face",
    "eyes",
    "mouth",
    "weapons",
  ];

  return (
  <div
    style={{
      position: "relative",
      width: size,
      height: size,
      flexShrink: 0,
      margin: "0 auto 10px",
    }}
  >
      {parts.map((part) => {
        const defaultSrc =
          `/assets/${part}/${side}/Common/1_thumb.png`;

        const src = getSkinSrc(
          seasonName,
          part,
          defaultSrc
        );

        return (
          <img
            key={part}
            src={src}
            alt=""
            draggable={false}
            onError={(e) => {
              if (e.currentTarget.src !== defaultSrc) {
                e.currentTarget.src = defaultSrc;
              }
            }}
            style={{
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: "100%",
  height: "100%",
  objectFit: "contain",
  pointerEvents: "none",
}}
          />
        );
      })}
    </div>
  );
}

function ItemTile({
  inv,
  user,
  onClick,
  busy,
}) {
  if (!inv) return null;

  const isClaimed = Boolean(inv.claimed);
  const cosmetic = isNameCosmetic(inv);
  const active = cosmetic && isNameCosmeticActive(inv, user);

  const disabled = isClaimed && !cosmetic;

  const quantityLabel = getItemQuantityLabel(inv);
  const iconStyle = getItemIconStyle(inv);

  const itemType = normalizeItemType(inv?.item?.type);

const showFullSeasonalSkin =
  itemType === "seasonal_accessory" &&
  getSeasonalFragmentCount(inv) === 1;

  let buttonText = "CLAIM";

  if (busy) {
    buttonText = "...";
  } else if (cosmetic) {
    buttonText = active ? "DISABLE" : "USE IT";
  } else if (isClaimed) {
    buttonText = "CLAIMED";
  }

  return (
    <div
      onClick={() => {
        if (!disabled && !busy) {
          onClick?.(inv);
        }
      }}
      role="button"
      tabIndex={0}
      style={{
        backgroundImage: 'url("/v2/item.png")',
        backgroundRepeat: "repeat",
        backgroundSize: "100% 100%",
        backgroundPosition: "center center",
        position: "relative",
        minHeight: 132,
        padding: 12,
        paddingBottom: 20,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",

        cursor:
          disabled || busy
            ? "default"
            : "pointer",

        opacity:
          isClaimed && !cosmetic
            ? 0.55
            : 1,

        filter: active
          ? "drop-shadow(0 0 6px rgba(87,255,107,0.9))"
          : "none",
      }}
    >
      {active && (
        <div
          className="shadow1"
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            zIndex: 5,
            padding: "2px 5px",
            borderRadius: 5,
            background: "rgba(31,150,50,0.95)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          ACTIVE
        </div>
      )}

     {showFullSeasonalSkin ? (
  <SeasonalSkinItemPreview
    inv={inv}
    user={user}
    size={76}
  />
) : isNameStyleItem(inv) ? (
  <NameStyleSample inv={inv} />
) : (
  <img
    src={getItemIconSrc(inv)}
    alt={getItemName(inv)}
    draggable={false}
    onError={(e) => {
      e.currentTarget.onerror = null;
      e.currentTarget.src = "/pack.png";
    }}
    style={{
      objectFit: "contain",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.7))",
      display: "block",
      ...iconStyle,
      maxWidth: "99%",
    }}
  />
)}

      <div className="claim_bt shadow1">
        {buttonText}
      </div>

      <div
        style={{
          color: "#fff",
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "14px",
          textShadow: "1px 1px #000",
          maxWidth: "100%",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          whiteSpace: "normal",
        }}
      >
        {getItemName(inv)}
      </div>

      {quantityLabel && (
        <div
          className="shadow1"
          style={{
            color: "#ffdf5f",
            fontSize: 13,
            fontWeight: 600,
            marginTop: 3,
          }}
        >
          {quantityLabel}
        </div>
      )}
    </div>
  );
}

const CHEST_REWARD_META = {
  sparks: { label: "Sparks", icon: "/sparks.png" },
  runes: { label: "Runes", icon: "/runes.png" },
  cardPieces: { label: "Card Pieces", icon: "/pieces.png" },
  cardpieces: { label: "Card Pieces", icon: "/pieces.png" },
    spins: { label: "Spins", icon: "/fortuneicon.png" },
  trophyShield: { label: "Trophy Shield", icon: "/shield.png" },
  shield: { label: "Trophy Shield", icon: "/shield.png" },
  tapi: { label: "TAPI", icon: "/icon_tapi.png" },
  evolve: { label: "Evolve", icon: "/evolveicon.png" },
  booster: { label: "Booster", icon: "/energy.png" },
  boosters: { label: "Booster", icon: "/energy.png" },
  ability: { label: "Ability", icon: "/fightlog.png" },
  abilities: { label: "Ability", icon: "/fightlog.png" },
  skins: { label: "Skin", icon: "/fragment.png" },
  seasonal_accessory: { label: "Skin", icon: "/fragment.png" },
  common: { label: "Common Card", icon: "/common.png" },
  rare: { label: "Rare Card", icon: "/rare.png" },
  epic: { label: "Epic Card", icon: "/epic.png" },
  legendary: { label: "Legendary Card", icon: "/legendary.png" },
};

function mapChestRewardsToRows(openResult) {
  const rewards =
    openResult?.rewards ||
    openResult?.reward ||
    openResult?.items ||
    {};

  if (Array.isArray(rewards)) {
    return rewards
      .map((reward, idx) => {
        const type = normalizeItemType(
          reward?.type ||
            reward?.itemType ||
            reward?.rewardType ||
            reward?.key ||
            ""
        );

        const meta = CHEST_REWARD_META[type] || {
          label: reward?.label || reward?.name || type || "Reward",
          icon: reward?.icon || "/pack.png",
        };

        return {
          id: `${type || "reward"}_${idx}`,
          type,
          label: reward?.label || reward?.name || meta.label,
          icon: reward?.icon || meta.icon,
          amount: Number(
            reward?.amount ??
              reward?.quantity ??
              reward?.qty ??
              reward?.value ??
              1
          ) || 1,
          extra:
            reward?.cardName ||
            reward?.card_name ||
            reward?.name ||
            "",
        };
      })
      .filter((reward) => reward.amount > 0);
  }

  return Object.entries(rewards || {})
    .map(([rawType, rawAmount]) => {
      const type = normalizeItemType(rawType);
      const meta = CHEST_REWARD_META[type] || {
        label: rawType,
        icon: "/pack.png",
      };

      const extra =
        type === "cardPieces"
          ? openResult?.cardPiecesCardName ||
            openResult?.cardPiecesCardId ||
            ""
          : "";

      return {
        id: `${type}_${Math.random().toString(16).slice(2)}`,
        type,
        label: meta.label,
        icon: meta.icon,
        amount: Number(rawAmount) || 0,
        extra,
      };
    })
    .filter((reward) => reward.amount > 0);
}

export default function ItemsInventory({ user, setUser, onClose, onChanged, onNavigate }) {
  const [items, setItems] = useState([]);
const [chests, setChests] = useState([]);

  const [tab, setTab] = useState("unclaimed");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

 const [claimModalOpen, setClaimModalOpen] = useState(false);
const [claimTarget, setClaimTarget] = useState(null);
const [claimBusy, setClaimBusy] = useState(false);
const [claimError, setClaimError] = useState("");

const [claimSelectedCardId, setClaimSelectedCardId] = useState("");
const [claimSelectedCard, setClaimSelectedCard] = useState(null);
const [claimCardPickerOpen, setClaimCardPickerOpen] = useState(false);

  const [openedChestModal, setOpenedChestModal] = useState(null);
  const [potionResultModal, setPotionResultModal] = useState(null);
  const [cosmeticBusyId, setCosmeticBusyId] = useState(null);


  const loadItemsPage = async () => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Not logged in.");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    };

   const [itemsRes, chestsRes] = await Promise.all([
  fetch(`${API_BASE}/api/inventory`, { headers }),
  fetch(`${API_BASE}/api/arenas/chests/all`, { headers }),
]);

const [itemsJson, chestsJson] = await Promise.all([
  itemsRes.json().catch(() => ({})),
  chestsRes.json().catch(() => ({})),
]);

if (!itemsRes.ok) {
  throw new Error(itemsJson.error || "Failed to load items.");
}

if (!chestsRes.ok) {
  throw new Error(chestsJson.error || "Failed to load chests.");
}

setItems(Array.isArray(itemsJson.items) ? itemsJson.items : []);
setChests(Array.isArray(chestsJson.chests) ? chestsJson.chests : []);
  };

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setLoading(true);
        setError("");
        await loadItemsPage();
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load items.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(() => {
    const chestRows = buildChestRows(chests);

    const normalItems = items.filter(
      (inv) =>
        !["runes", "sparks"].includes(
          String(inv?.item?.type || "").toLowerCase()
        )
    );

    return [...chestRows, ...normalItems].sort(
      (a, b) => Number(!!a.claimed) - Number(!!b.claimed)
    );
  }, [items, chests]);

  const unclaimedRows = rows.filter((inv) => !inv.claimed);
  const claimedRows = rows.filter((inv) => inv.claimed);

  const visibleRows = tab === "claimed" ? claimedRows : unclaimedRows;

  const toggleNameCosmetic = async (inv) => {
  if (
    !inv?.user_item_id ||
    cosmeticBusyId ||
    claimBusy
  ) {
    return;
  }

  const active = isNameCosmeticActive(inv, user);
  const action = active ? "remove" : "equip";

  try {
    setCosmeticBusyId(inv.user_item_id);
    setClaimError("");

    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("Not logged in.");
    }

    const res = await fetch(
      `${API_BASE}/api/inventory/equip-name-item`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA":
            window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({
          userItemId: inv.user_item_id,
          action,
        }),
      }
    );

    const data = await res.json().catch(() => ({}));

if (!res.ok) {
  throw new Error(data.error || "Failed to claim item.");
}

if (data.user && typeof setUser === "function") {
  setUser((prev) => ({
    ...(prev || {}),
    ...(data.user || {}),
  }));
}

onChanged?.();
  } catch (err) {
    setError(
      err.message || "Failed to update cosmetic."
    );
  } finally {
    setCosmeticBusyId(null);
  }
};
const openClaimModal = (inv) => {
  if (
    !inv ||
    claimBusy ||
    cosmeticBusyId
  ) {
    return;
  }

  if (isNameCosmetic(inv)) {
    toggleNameCosmetic(inv);
    return;
  }

  if (inv.claimed) {
    return;
  }

  setClaimTarget(inv);
  setClaimError("");

  setClaimSelectedCardId("");
  setClaimSelectedCard(null);
  setClaimCardPickerOpen(false);

  setClaimModalOpen(true);
};

const closeClaimModal = () => {
  if (claimBusy) return;

  setClaimModalOpen(false);
  setClaimTarget(null);
  setClaimError("");

  setClaimSelectedCardId("");
  setClaimSelectedCard(null);
  setClaimCardPickerOpen(false);
};

const openChest = async (inv) => {
    const chestId = inv?.chest?.id;

    if (!chestId) {
      setClaimError("Missing chest id.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) throw new Error("Not logged in.");

    const res = await fetch(
      `${API_BASE}/api/arenas/chests/open-guaranteed-sparks`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({ chestId }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.error || "Failed to open chest.");
    }

    if (data.user && typeof setUser === "function") {
      setUser((prev) => ({
        ...(prev || {}),
        ...(data.user || {}),
      }));
    }

    return data;
  };

  const confirmClaim = async () => {
  if (!claimTarget || claimBusy) return;

  const claimedType = normalizeItemType(
    claimTarget?.item?.type
  );

  const requiresCard = needsCardSelection(claimTarget);

    if (requiresCard && !claimSelectedCardId) {
      setClaimError("Please choose which card to apply this item to.");
      return;
    }

    try {
      setClaimBusy(true);
      setClaimError("");

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Not logged in.");

    if (isArenaChestRow(claimTarget)) {
  const opened = await openChest(claimTarget);

  setOpenedChestModal({
    chestName: getItemName(claimTarget),
    chestIcon: getItemIconSrc(claimTarget),
    rewards: mapChestRewardsToRows(opened),
  });
} else {
        const userItemId = claimTarget.user_item_id;

        if (!userItemId) {
          setClaimError("Missing item identifier.");
          return;
        }

        const body = requiresCard ? { card_id: claimSelectedCardId } : {};

        const res = await fetch(
  `${API_BASE}/api/inventory/${encodeURIComponent(userItemId)}/claim`,
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    },
    body: JSON.stringify(body),
  }
);

const data = await res.json().catch(() => ({}));

if (!res.ok) {
  throw new Error(data.error || "Failed to claim item.");
}

if (data.user && typeof setUser === "function") {
  setUser((prev) => ({
    ...(prev || {}),
    ...(data.user || {}),
  }));
}

if (claimedType === "magic_potion" && data.magicPotion) {
  setPotionResultModal({
    itemName: getItemName(claimTarget),
    sparksSpent: Number(data.magicPotion.sparksSpent || 0),
    runesReceived: Number(data.magicPotion.runesReceived || 0),
    winningChance: Number(data.magicPotion.winningChance || 0),
    sparksBalance: Number(data.user?.sparks || 0),
    runesBalance: Number(data.user?.runes || 0),
  });
}

if (
  claimedType === "chest" &&
  data.chest
) {
  setOpenedChestModal({
    chestName: getItemName(claimTarget),
    chestIcon: getItemIconSrc(claimTarget),
    rewards: mapChestRewardsToRows(data.chest),
  });
}

      }

      await loadItemsPage();

      setClaimModalOpen(false);
setClaimTarget(null);

setClaimSelectedCardId("");
setClaimSelectedCard(null);
setClaimCardPickerOpen(false);
      onChanged?.();
      if (claimedType === "booster") {
        onNavigate?.("home");
      }
    } catch (err) {
      setClaimError(err.message || "Failed to claim item.");
    } finally {
      setClaimBusy(false);
    }
  };


  return (
    <div className="modal_box">
      <div className="modal_title">
        Inventory Items
        <button type="button" className="modal_close" onClick={onClose} />
      </div>

      <div className="modal_body">
        <div className="modal_inside_2">
          <div
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <button
  type="button"
  className="game-button yellow-button"
  onClick={() => setTab("unclaimed")}
  style={{
    flex: 1,
    minWidth: 0,
    opacity: tab === "unclaimed" ? 1 : 0.45,
  }}
>
  TO CLAIM ({unclaimedRows.length})
</button>

<button
  type="button"
  className="game-button yellow-button"
  onClick={() => setTab("claimed")}
  style={{
    flex: 1,
    minWidth: 0,
    opacity: tab === "claimed" ? 1 : 0.65,
  }}
>
  CLAIMED ({claimedRows.length})
</button>
          </div>

          {loading && (
  <div
    style={{
      minHeight: 330,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner text="Loading Items" size={58} />
  </div>
)}

          {!loading && error && (
            <div style={{ padding: 16, color: "salmon", textAlign: "center" }}>
              {error}
            </div>
          )}

          {!loading && !error && visibleRows.length === 0 && (
            <div className="info">
            <div
              className="shadow1"
              style={{
                fontWeight: 600,
                fontSize: 21,
                textAlign: "center",
              }}
            >
              {tab === "unclaimed"
                ? "No unclaimed items."
                : "No claimed items yet."}
            </div></div>
          )}

          {!loading && !error && visibleRows.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {visibleRows.map((inv) => (
  <ItemTile
    key={inv.user_item_id}
    inv={inv}
    user={user}
    onClick={openClaimModal}
    busy={
      cosmeticBusyId === inv.user_item_id ||
      (
        claimBusy &&
        claimTarget?.user_item_id ===
          inv.user_item_id
      )
    }
  />
))}
            </div>
          )}
        </div>

        <div className="modal_footer">
          <button
  type="button"
  className="game-button green-button"
  onClick={() => onNavigate?.("shop", { initialView: "items" })}
>
  BUY NEW ITEMS
</button>
        </div>

      </div>

      {claimModalOpen && claimTarget && !claimCardPickerOpen && (
        <div className="modal_box">
          <div className="modal_title">
            {isChestRow(claimTarget) ? "Open Chest" : "Confirm Claim"}

            <button
              type="button"
              className="modal_close"
              onClick={closeClaimModal}
            />
          </div>

          <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
            <div className="modal_inside">
             {normalizeItemType(claimTarget?.item?.type) === "seasonal_accessory" &&
getSeasonalFragmentCount(claimTarget) === 1 ? (
  <SeasonalSkinItemPreview
    inv={claimTarget}
    user={user}
    size={110}
  />
) : (
  <img
    src={getItemIconSrc(claimTarget)}
    alt=""
    style={{
      height: 72,
      objectFit: "contain",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.7))",
      marginBottom: 10,
    }}
  />
)}

              <div
                className="shadow2"
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                {isChestRow(claimTarget) ? "Open Chest?" : "Claim Item?"}
              </div>

              <div
                className="info"
                style={{
                  color: "#fff",
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                {getItemName(claimTarget)}
              </div>

              {getItemQuantityLabel(claimTarget) && (
                <div
                  className="shadow1"
                  style={{
                    color: "#ffdf5f",
                    fontSize: 20,
                    fontWeight: 600,
                    marginBottom: 10,
                  }}
                >
                  {getItemQuantityLabel(claimTarget)}
                </div>
              )}

              {needsCardSelection(claimTarget) && (
  <div
    className="info"
  >

    {claimSelectedCard ? (
      <div
        style={{
          background: "rgba(0,0,0,0.35)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 10,
          padding: "10px 12px",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            color: "#fff",
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 3,
          }}
        >
          {claimSelectedCard.name ||
            `Card #${claimSelectedCard.id}`}
        </div>

        <div
          style={{
            color: "#ffdf5f",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {claimSelectedCard.rarity || "Common"}
          {" • "}
          Lv {claimSelectedCard.level || 1}
        </div>
      </div>
    ) : (
      <div
        style={{
          color: "rgba(255,255,255,0.7)",
          fontSize: 14,
          marginBottom: 10,
        }}
      >
        No card selected.
      </div>
    )}

    <button
      type="button"
      className="game-button yellow-button"
      disabled={claimBusy}
      onClick={() => {
        setClaimError("");
        setClaimCardPickerOpen(true);
      }}
      style={{
        width: "100%",
        opacity: claimBusy ? 0.5 : 1,
      }}
    >
      {claimSelectedCard ? "CHANGE CARD" : "SELECT CARD"}
    </button>
  </div>
)}


              {claimError && (
                <div

                  className="info"
                  style={{
                    color: "#d4c342",
                  }}
                >
                 {claimError}
                </div>
              )}
            </div>

            <div className="modal_footer">
              <button
                type="button"
                className="game-button green-button"
                disabled={claimBusy}
                onClick={confirmClaim}
                style={{
                  marginRight: 10,
                  opacity: claimBusy ? 0.5 : 1,
                  cursor: claimBusy ? "not-allowed" : "pointer",
                }}
              >
                {claimBusy
                  ? isChestRow(claimTarget)
                    ? "Opening..."
                    : "Claiming..."
                  : isChestRow(claimTarget)
                  ? "OPEN"
                  : "CLAIM"}
              </button>

              <button
                type="button"
                className="game-button red-button"
                disabled={claimBusy}
                onClick={closeClaimModal}
                style={{
                  opacity: claimBusy ? 0.5 : 1,
                  cursor: claimBusy ? "not-allowed" : "pointer",
                }}
              >
                CANCEL
              </button>
            </div>
          </motion.div>
        </div>
      )}

            {claimCardPickerOpen && claimTarget && (
        <div className="modal_box">
          <div className="modal_title">
            Select Card

            <button
              type="button"
              className="modal_close"
              onClick={() => {
                setClaimCardPickerOpen(false);
              }}
            />
          </div>

          <CardPicker
            user={user}
            onNavigate={onNavigate}
            openedVia="itemClaim"
            persistSelection={false}
            showBuyButton={false}
            onClose={() => {
              setClaimCardPickerOpen(false);
            }}
            onSelected={({ cardId, card }) => {
              setClaimSelectedCardId(String(cardId));
              setClaimSelectedCard(card);
              setClaimError("");
              setClaimCardPickerOpen(false);
            }}
          />
        </div>
      )}

      {potionResultModal && (
  <div className="modal_box" style={{ zIndex: 99999 }}>
    <div className="modal_title">
      Potion Complete

      <button type="button" className="modal_close" onClick={() => setPotionResultModal(null)} />
    </div>

    <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
      <div className="modal_inside">
        <img
          src="/magic_potion.png"
          alt=""
          draggable={false}
          style={{
            height: 110,
            objectFit: "contain",
            marginBottom: 5,
            filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.7))",
          }}
        />

        <div
          className="shadow2"
          style={{
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 5,
          }}
        >
          You Got
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginTop: 5,
          }}
        >
          <img
            src="/runes.png"
            alt=""
            draggable={false}
            style={{
              width: 32,
              height: 32,
              objectFit: "contain",
            }}
          />

          <div
            className="shadow2"
            style={{
              color: "#ffdf5f",
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            +{potionResultModal.runesReceived.toLocaleString()} Runes
          </div>
        </div>

        <div className="info">
          <div
            className="shadow1"
            style={{
              color: "#fff",
              fontSize: 16,
              lineHeight: "24px",
              fontWeight: 600,
            }}
          >
            <span style={{ color: "#ffdf5f" }}>
              {potionResultModal.sparksSpent.toLocaleString()} Sparks
            </span>{" "}
            were converted.
          </div>

        </div>
      </div>

      <div className="modal_footer">
        <button type="button" className="game-button green-button" onClick={() => setPotionResultModal(null)}>
          AWESOME
        </button>
      </div>
    </motion.div>
  </div>
)}

      {openedChestModal && (
  <div
    className="modal_box"
    style={{ zIndex: 99999 }}
  >
    <div className="modal_title">
      Chest Opened
      <button
        type="button"
        className="modal_close"
        onClick={() => setOpenedChestModal(null)}
      />
    </div>

    <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
      <div className="modal_inside">
        <img
          src="/v2/chest_open.png"
          alt=""
          style={{
            height: 120,
            objectFit: "contain",
            marginTop: -10,
            marginBottom: 8,
            filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.7))",
          }}
        />

        <div
          className="shadow2"
          style={{
            fontSize: 30,
            fontWeight: 700,
            marginBottom: 8,
            textTransform: "uppercase",
          }}
        >
          You Got
        </div>

        <div
          className="shadow1"
          style={{
            color: "#ffdf5f",
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          {openedChestModal.chestName}
        </div>

        {openedChestModal.rewards.length > 0 ? (
          <div className="info" style={{ paddingTop: 1 }}>
            {openedChestModal.rewards.map((reward) => (
              <div
                key={reward.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "rgba(0,0,0,0.25)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  marginTop: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minWidth: 0,
                  }}
                >
                  <img
                    src={reward.icon}
                    alt=""
                    style={{
                      width: 38,
                      height: 38,
                      objectFit: "contain",
                    }}
                  />

                  <div
                    style={{
                      textAlign: "left",
                      minWidth: 0,
                    }}
                  >
                    <div
                      className="shadow1"
                      style={{
                        fontSize: 18,
                        color: "#fff",
                        lineHeight: "18px",
                        fontWeight: 700,
                      }}
                    >
                      {reward.label}
                    </div>

                    {reward.extra && (
                      <div
                        className="shadow1"
                        style={{
                          fontSize: 13,
                          color: "rgba(255,255,255,0.82)",
                          marginTop: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 170,
                        }}
                      >
                        {reward.extra}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="shadow1"
                  style={{
                    fontSize: 24,
                    color: "#ffdf5f",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  +{Number(reward.amount || 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="info">
            <div
              className="shadow1"
              style={{
                color: "#fff",
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              Chest opened successfully.
            </div>
          </div>
        )}
      </div>

      <div className="modal_footer">
        <button
          type="button"
          className="game-button green-button"
          onClick={() => setOpenedChestModal(null)}
        >
          COLLECT
        </button>
      </div>
    </motion.div>
  </div>
)}
    </div>
  );
}